// @flow
import moment from 'moment';
import { get } from 'lodash';

import { formatApiDate, API_DATE_FORMAT } from 'Utils/date/apiDate';
import { COLORS } from 'Components/utils/color/colors';
import { type PostAnswerProps } from 'Components/frameworks/post-answer/PostAnswer.jsx';
import { type PostQuestionProps } from 'Components/frameworks/post-question/PostQuestion.jsx';

type Question = {
  ...$Exact<PostQuestionProps>,
  answers: Array<PostAnswerProps>,
  trackingData?: TrackingData,
};

const DATE_FORMAT = 'D MMM YYYY';

function getTag({ idx, lastAnswerLabel }) {
  return idx === 0
    ? {
        color: COLORS.WATER_GREEN,
        label: lastAnswerLabel,
      }
    : null;
}

function mapAnswer(answers, labels = {}) {
  const { lastAnswerLabel, usefullLabel } = labels;

  return answers.map<PostAnswerProps>((a, idx) => {
    const tag = getTag({ idx, lastAnswerLabel });
    return {
      id: a.id,
      author: a.authorName,
      date: formatApiDate(a.date, DATE_FORMAT),
      likesCount: a.likesCount,
      likesLabel: usefullLabel,
      ...(tag ? { tag } : null),
      text: a.text,
    };
  });
}

function mapQuestionsAndAnswers(props) {
  const {
    bestAnswerLabel,
    lastAnswerLabel,
    usefullLabel,
    productCommunity: { questions = [] },
  } = props;

  const avatarsColors = [COLORS.AMBER, COLORS.OCHER, COLORS.PINE];

  return questions
    .sort(
      (a, b) =>
        moment(b.date, API_DATE_FORMAT)
          .toDate()
          .getTime() -
          moment(a.date, API_DATE_FORMAT)
            .toDate()
            .getTime() || a.likesCount - b.likesCount,
    )
    .map<Question>((q, idx) => ({
      id: q.id,
      answers: mapAnswer(q.answers, { bestAnswerLabel, lastAnswerLabel, usefullLabel }),
      answersCount: q.answersCount,
      author: {
        color: avatarsColors[idx % avatarsColors.length],
        name: q.authorName,
      },
      date: formatApiDate(q.date, DATE_FORMAT),
      likesCount: q.likesCount,
      text: q.text,
      title: q.title,
      trackingData: {
        event: 'eventGA',
        categoryGA: 'resort',
        actionGA: 'wibilong',
        labelGA: 'seeAnswers',
        valueGA: '0',
        nonInteraction: '0',
      },
    }));
}

export type ProductCommunityQuestionsDescriptorProps = {
  bestAnswerLabel: string,
  buttonLabel: string,
  lastAnswerLabel: string,
  noAnswersLabel: string,
  questionsLabel: string,
  resortId: string,
  seeAllQuestionsLabel: string,
  seeMoreLabel: string,
  text: string,
  usefullLabel: string,
};

export type ProductCommunityQuestionsLineProps = {
  ...$Exact<ProductCommunityQuestionsDescriptorProps>,
  config: {
    common: { next: string, previous: string },
  },
  productCommunity: {
    linkAllQuestionsUrl: string,
    questions: Array<{
      answers: Array<{
        authorName: string,
        date: string,
        id: string,
        likesCount: number,
        text: string,
      }>,
      answersCount: number,
      authorName: string,
      date: string,
      id: string,
      likesCount: number,
      text: string,
      title: string,
    }>,
  },
  resort: { icon: string },
};

type ProductCommunityQuestionsMapperProps = {
  ...$Exact<ProductCommunityQuestionsLineProps>,
  popinButton: {|
    country: string,
    language: string,
    productId: string,
  |},
};

export function mapCommunityQuestions(props: ProductCommunityQuestionsMapperProps) {
  const {
    buttonLabel,
    config,
    noAnswersLabel,
    popinButton,
    productCommunity: { linkAllQuestionsUrl },
    resort: { icon },
    seeAllQuestionsLabel,
    seeMoreLabel,
    text,
  } = props;

  return {
    button: {
      label: buttonLabel,
      ...popinButton,
      trackingData: {
        event: 'eventGA',
        categoryGA: 'resort',
        actionGA: 'wibilong',
        labelGA: 'askQuestion',
        valueGA: '0',
        nonInteraction: '0',
      },
    },
    icon,
    linkAllQuestions: linkAllQuestionsUrl
      ? {
          label: seeAllQuestionsLabel,
          target: '_blank',
          url: linkAllQuestionsUrl,
          trackingData: {
            event: 'eventGA',
            categoryGA: 'resort',
            actionGA: 'wibilong',
            labelGA: 'seeQuestion',
            valueGA: '0',
            nonInteraction: '0',
          },
        }
      : {},
    noAnswerstext: noAnswersLabel,
    questions: mapQuestionsAndAnswers(props),
    scrollBackwardLabel: get(config, 'common.scrollBackward'),
    scrollForwardLabel: get(config, 'common.scrollForward'),
    seeMoreLabel,
    text,
  };
}
